import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, SimpleChanges, OnChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { ViewportService } from '@shared/common/services/viewport/viewport.service';
import { ResizeService } from '../../../core/services/resize/resize.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-read-more',
  template: `
    <ng-container *ngIf="text">
      <span [innerHTML]="text"></span>
      <ng-container *ngIf="!showOnlyText && (isMobile || !this.showOnlyMobile)">
        <span *ngIf="!isFullText">...</span>
        <ng-container *ngIf="isFullText" />
        <br />
        <a
          *ngIf="!showOnlyText"
          [ngStyle]="{
            display: 'inline-block',
            color: linkColor,
            cursor: 'pointer',
            'font-weight': fontWeight,
            'text-decoration': textDecoration,
            'font-size': '16px',
            'line-height': '24px',
            margin: '8px 0 0 0'
          }"
          (click)="toggleReadMore()"
        >
          <span *ngIf="!hide">{{ 'unit-group-suite-info-read-more' | translate }}</span>
          <span *ngIf="hide">{{ 'unit-group-suite-info-read-less' | translate }}</span>
        </a>
      </ng-container>
    </ng-container>
  `,
  standalone: false
})
export class ReadMoreComponent implements OnInit, OnChanges, OnDestroy {
  @Input() text: string;
  @Input() textLength = 100;
  @Input() linkColor = '#3D8F86';
  @Input() fontWeight = 600;
  @Input() showOnlyText = true;
  @Input() showOnlyMobile = true;
  @Input() textDecoration = 'underline';
  @Output() readMore: EventEmitter<any> = new EventEmitter();
  public showMoreText: string;
  public hide = true;
  isFullText = false;
  private readonly _destruction$: Subject<void> = new Subject<void>();

  constructor(
    private readonly resizeService: ResizeService,
    private readonly viewportService: ViewportService
  ) {}

  get isMobile() {
    return this.viewportService.isMobileDevice();
  }

  ngOnInit() {
    this.resizeService
      .resize()
      .pipe(takeUntil(this._destruction$))
      .subscribe(() => {
        this.onResize();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const { text } = changes || {};

    if (!text?.currentValue) {
      return;
    }

    this.text = text.currentValue;
    this.showMoreText = this.text;

    if (this.showOnlyText) {
      return;
    }

    if (this.text.length <= this.textLength) {
      this.showOnlyText = true;
      return;
    }

    if (!this.isMobile && this.showOnlyMobile) {
      return;
    }

    this.isFullText ? (this.hide = false) : (this.hide = true);
    this.toggleLength(false);
  }

  onResize(): void {
    // on desktop
    if (!this.isMobile && this.showOnlyMobile) {
      this.hide = false;
      this.text = this.showMoreText;
      return;
    }

    // on mobile
    if (this.showOnlyText) {
      this.hide = true;
      this.text = this.showMoreText;
      return;
    }

    this.isFullText ? (this.hide = false) : (this.hide = true);
    this.toggleLength();
  }

  ngOnDestroy() {
    this._destruction$.next();
    this._destruction$.complete();
  }

  toggleReadMore() {
    this.isFullText = !this.isFullText;
    this.toggleLength();
  }

  toggleLength(emitEvent = true) {
    if (this.showMoreText.length <= this.textLength || !this.hide) {
      this.hide = true;
      this.text = this.showMoreText;

      if (emitEvent) {
        this.readMore.emit(!!this.hide);
      }
      return;
    }

    this.hide = false;
    if (this.showMoreText.length > this.textLength) {
      //trim the string to the maximum length
      this.text = this.showMoreText.substr(0, this.textLength);
      //re-trim if we are in the middle of a word
      this.text = this.text.substr(0, Math.min(this.text.length, this.text.lastIndexOf(' ')));
    }

    if (emitEvent) {
      this.readMore.emit(!!this.hide);
    }
  }
}
